import * as React from 'react';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

export interface StatusBadgeProps {
  status: string;
  variant?: 'dot' | 'pill' | 'outline';
  size?: 'sm' | 'md';
  className?: string;
}

// Map status strings to colors
const statusColorMap: Record<string, 'success' | 'warning' | 'destructive' | 'secondary' | 'primary'> = {
  // Green (success)
  approved: 'success',
  active: 'success',
  present: 'success',
  completed: 'success',

  // Yellow (warning)
  pending: 'warning',
  processing: 'warning',

  // Red (destructive)
  rejected: 'destructive',
  absent: 'destructive',
  failed: 'destructive',

  // Gray (secondary)
  draft: 'secondary',
  weekend: 'secondary',
  inactive: 'secondary',

  // Blue (primary)
  'on-leave': 'primary',
  holiday: 'primary',
};

// Normalize status string for lookup
function normalizeStatus(status: string): string {
  return status.toLowerCase().trim();
}

// Get color variant for a status
function getColorVariant(status: string): 'success' | 'warning' | 'destructive' | 'secondary' | 'primary' {
  const normalized = normalizeStatus(status);
  return statusColorMap[normalized] || 'secondary';
}

// Capitalize for display
function capitalizeStatus(status: string): string {
  return status
    .split(/[\s-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export const StatusBadge = React.forwardRef<
  HTMLDivElement,
  StatusBadgeProps
>(({ status, variant = 'pill', size = 'md', className }, ref) => {
  const colorVariant = getColorVariant(status);
  const displayStatus = capitalizeStatus(status);

  // Determine badge classes based on variant and size
  let badgeVariant: 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'outline' = colorVariant as any;
  if (variant === 'outline') {
    badgeVariant = 'outline';
  }

  const dotClasses = {
    sm: 'h-1.5 w-1.5',
    md: 'h-2 w-2',
  };

  const dotColorMap: Record<'success' | 'warning' | 'destructive' | 'secondary' | 'primary', string> = {
    success: 'bg-[var(--color-success)]',
    warning: 'bg-[var(--color-warning)]',
    destructive: 'bg-[var(--color-destructive)]',
    secondary: 'bg-[var(--color-secondary)]',
    primary: 'bg-[var(--color-primary)]',
  };

  return (
    <div
      ref={ref}
      className={cn('inline-flex items-center gap-2', className)}
    >
      {variant === 'dot' && (
        <div className={cn('rounded-full', dotClasses[size], dotColorMap[colorVariant])} />
      )}
      <Badge variant={badgeVariant} className={size === 'sm' ? 'text-xs' : 'text-sm'}>
        {displayStatus}
      </Badge>
    </div>
  );
});

StatusBadge.displayName = 'StatusBadge';
